/**
 * CodeMirror 6 Editor Component with Syntax Highlighting
 * Lightweight tree-sitter-style parsing via Lezer
 */

import React from 'react'
import CodeMirror from '@uiw/react-codemirror'
import { javascript } from '@codemirror/lang-javascript'
import { python } from '@codemirror/lang-python'
import { java } from '@codemirror/lang-java'
import { cpp } from '@codemirror/lang-cpp'
import { rust } from '@codemirror/lang-rust'
import { go } from '@codemirror/lang-go'
import { php } from '@codemirror/lang-php'
import { sql } from '@codemirror/lang-sql'
import { EditorView } from '@codemirror/view'
import type { Extension } from '@codemirror/state'

interface CodeMirrorEditorProps {
  value: string
  onChange: (value: string) => void
  language: string
  placeholder?: string
  height?: string
  className?: string
}

/**
 * Get CodeMirror language extension based on language code
 */
const getLanguageExtension = (language: string): Extension => {
  const langLower = language.toLowerCase()

  switch (langLower) {
    case 'javascript':
      return javascript({ jsx: true })
    case 'typescript':
      return javascript({ jsx: true, typescript: true })
    case 'python':
      return python()
    case 'java':
      return java()
    case 'kotlin':
      return java() // Kotlin uses Java syntax highlighting as fallback
    case 'c':
    case 'c++':
    case 'cpp':
    case 'objective-c':
    case 'c#':
      return cpp()
    case 'rust':
      return rust()
    case 'go':
      return go()
    case 'php':
      return php()
    case 'sql':
      return sql()
    case 'swift':
      return cpp() // Use C++ as fallback for Swift
    case 'r':
      return python() // Use Python as fallback for R (similar syntax)
    case 'julia':
      return python() // Use Python as fallback for Julia (similar syntax)
    default:
      return javascript() // Default fallback
  }
}

/**
 * Custom dark theme matching the existing textarea style
 */
const darkTheme = EditorView.theme({
  '&': {
    backgroundColor: '#111827', // bg-gray-900
    color: '#f3f4f6', // text-gray-100
    height: '100%',
  },
  '.cm-content': {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: '12px',
    lineHeight: '1.25rem',
    padding: '12px',
    caretColor: '#f97316', // orange-500
  },
  '.cm-gutters': {
    backgroundColor: '#1f2937', // bg-gray-800
    color: '#6b7280', // text-gray-500
    border: 'none',
  },
  '.cm-activeLineGutter': {
    backgroundColor: '#374151', // bg-gray-700
  },
  '.cm-activeLine': {
    backgroundColor: '#1f2937', // bg-gray-800
  },
  '.cm-selectionBackground, ::selection': {
    backgroundColor: '#374151 !important', // bg-gray-700
  },
  '.cm-focused .cm-selectionBackground, .cm-focused ::selection': {
    backgroundColor: '#374151 !important',
  },
  '.cm-cursor': {
    borderLeftColor: '#f97316', // orange-500
  },
  '&.cm-focused': {
    outline: '1px solid rgba(249, 115, 22, 0.2)', // ring-orange-500/20
  },
}, { dark: true })

export const CodeMirrorEditor: React.FC<CodeMirrorEditorProps> = ({
  value,
  onChange,
  language,
  placeholder = '',
  height = '100%',
  className = '',
}) => {
  const extensions = [
    getLanguageExtension(language),
    darkTheme,
    EditorView.lineWrapping, // Enable line wrapping
  ]

  return (
    <div className={`codemirror-wrapper ${className}`} style={{ height, overflow: 'auto' }}>
      <CodeMirror
        value={value}
        height={height}
        theme="dark"
        extensions={extensions}
        onChange={(value) => onChange(value)}
        placeholder={placeholder}
        basicSetup={{
          lineNumbers: true,
          highlightActiveLineGutter: true,
          highlightActiveLine: true,
          foldGutter: true,
          dropCursor: true,
          allowMultipleSelections: true,
          indentOnInput: true,
          bracketMatching: true,
          closeBrackets: true,
          autocompletion: false, // Disable autocomplete for now
          rectangularSelection: true,
          crosshairCursor: true,
          highlightSelectionMatches: true,
          closeBracketsKeymap: true,
          searchKeymap: true,
          foldKeymap: true,
          completionKeymap: false,
          lintKeymap: true,
        }}
      />
    </div>
  )
}

export default CodeMirrorEditor
